import { type PricingPlanTier, type TierProgress } from '@carsfinders/contracts';
import { useQuery } from '@tanstack/react-query';
import { useId, type ReactNode } from 'react';

import { t } from '../../../lib/i18n';
import { clientTierProgressKey, fetchClientTierProgress } from '../api';

const DATE_FORMAT = new Intl.DateTimeFormat('uk-UA', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const tierName = (tier: PricingPlanTier): string => t(`admin.tiers.${tier}`);

/** `04.10.2026`; `—` for an absent or unparseable instant. */
function formatDate(value: Date | string | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : DATE_FORMAT.format(date);
}

interface ClientTierCardProps {
  userId: string;
  /**
   * `users.planEvaluatedAt` — when the tier was last evaluated. Not part of
   * `TierProgress`; it comes off the client document the page already holds.
   */
  planEvaluatedAt?: string | null;
}

/**
 * «Рівень комісії» — why a client is on their tier (volume-tier commission,
 * SC-12). Every figure is the API's (`GET /admin/deals/clients/:id/tier-progress`):
 * the tier, the paid cars in the rolling window, the cars to the next tier and
 * the hold, plus when the tier was last evaluated. While the rule set in force
 * has no volume tiers it shows the stored plan and says so. Loading, error and
 * retry follow `bidding-mode-card.tsx`.
 */
export function ClientTierCard({ userId, planEvaluatedAt = null }: ClientTierCardProps) {
  const headingId = useId();
  const query = useQuery({
    queryKey: clientTierProgressKey(userId),
    queryFn: () => fetchClientTierProgress(userId),
  });

  return (
    <section
      aria-labelledby={headingId}
      data-testid="client-tier-card"
      className="flex flex-col gap-3 rounded-cf-lg border border-cf-line bg-cf-surface p-4 leading-[normal] lg:px-6 lg:py-[18px]"
    >
      <h2 id={headingId} className="m-0 text-[18px] font-semibold tracking-[-0.02em]">
        {t('admin.clientTier.title')}
      </h2>
      {query.isLoading ? (
        <p className="m-0 text-[13px] text-cf-ink-subtle" role="status">
          {t('admin.clientTier.loading')}
        </p>
      ) : query.isError || !query.data ? (
        <div role="alert" data-testid="client-tier-load-error" className="flex flex-col gap-2">
          <p className="m-0 text-[13px] text-cf-danger">{t('admin.clientTier.loadFailed')}</p>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="h-9 w-fit rounded-cf-md border border-cf-line bg-cf-surface px-3 text-[13px] font-semibold hover:border-cf-ink focus-visible:outline-2 focus-visible:outline-cf-ink"
          >
            {t('admin.clientTier.retry')}
          </button>
        </div>
      ) : (
        <TierFacts progress={query.data} planEvaluatedAt={planEvaluatedAt} />
      )}
    </section>
  );
}

function TierFacts({
  progress,
  planEvaluatedAt,
}: {
  progress: TierProgress;
  planEvaluatedAt: string | null;
}) {
  const evaluated = planEvaluatedAt
    ? formatDate(planEvaluatedAt)
    : t('admin.clientTier.notEvaluated');

  if (!progress.active) {
    return (
      <>
        <p className="m-0 text-[13px] text-cf-ink-muted" data-testid="client-tier-inactive">
          {t('admin.clientTier.inactive')}
        </p>
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[13px]">
          <Fact label={t('admin.clientTier.storedPlan')} testId="client-tier-tier">
            {tierName(progress.tier)}
          </Fact>
          <Fact label={t('admin.clientTier.evaluatedAt')} testId="client-tier-evaluated">
            {evaluated}
          </Fact>
        </dl>
      </>
    );
  }

  return (
    <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[13px]">
      <Fact label={t('admin.clientTier.tier')} testId="client-tier-tier">
        {tierName(progress.tier)}
      </Fact>
      <Fact label={t('admin.clientTier.paidInWindow')} testId="client-tier-paid">
        {t('admin.clientTier.paidInWindowValue', {
          count: progress.paidCountInWindow,
          days: progress.windowDays ?? '—',
        })}
      </Fact>
      {progress.nextTier !== null && progress.carsToNextTier !== null ? (
        <Fact
          label={t('admin.clientTier.nextTier', { tier: tierName(progress.nextTier) })}
          testId="client-tier-next"
        >
          {t('admin.clientTier.carsToNext', { count: progress.carsToNextTier })}
        </Fact>
      ) : (
        <Fact label={t('admin.clientTier.topTier')} testId="client-tier-next">
          —
        </Fact>
      )}
      {progress.holdUntil && (
        <Fact label={t('admin.clientTier.holdUntil')} testId="client-tier-hold">
          {formatDate(progress.holdUntil)}
        </Fact>
      )}
      <Fact label={t('admin.clientTier.evaluatedAt')} testId="client-tier-evaluated">
        {evaluated}
      </Fact>
    </dl>
  );
}

function Fact({ label, testId, children }: { label: string; testId: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-cf-ink-subtle">{label}</dt>
      <dd className="m-0 font-medium text-cf-ink" data-testid={testId}>
        {children}
      </dd>
    </>
  );
}
